import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiMapPin, FiPlus, FiSearch, FiFilter, FiEdit2 } from '../utils/icons';
import { getAllLocations, getOrganizations } from '../services/adminService';
import { Location, Organization } from '../types';
import { AddLocationModal } from '../components/location/AddLocationModal';
import { EditLocationModal } from '../components/location/EditLocationModal';

export const LocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  useEffect(() => {
    loadOrganizations();
    loadLocations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await getOrganizations(undefined, { page: 1, limit: 100 });
      if (response.success && response.data) {
        setOrganizations(response.data);
      }
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllLocations(undefined, { page: 1, limit: 1000 });
      if (response.success && response.data) {
        setLocations(response.data);
      } else {
        setError('Failed to load locations');
      }
    } catch (err) {
      setError('An error occurred while loading locations');
      console.error('Error loading locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuccess = () => {
    loadLocations();
  };

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    loadLocations();
    setSelectedLocation(null);
  };

  const getOrgName = (orgId: number | undefined): string => {
    if (!orgId) return 'Unknown';
    const org = organizations.find((o) => o.orgId === orgId);
    return org?.name || `Org #${orgId}`;
  };

  const getLocationAddress = (location: Location): string => {
    // Build detailed address from individual fields
    const parts = [
      location.plotNo,
      location.buildingName,
      location.buildingWing,
      location.floor && `Floor ${location.floor}`,
      location.officeUnit,
      location.street,
      location.landmark,
      location.zone,
      location.colony,
      location.sector,
      location.suburb,
      location.city,
      location.district,
      location.state,
      location.pinCode,
    ].filter(Boolean);

    // If we have detailed address parts, join them
    if (parts.length > 0) {
      return parts.join(', ');
    }

    // Fallback to the address field if available
    if (location.address) {
      return location.address;
    }

    return 'No address information';
  };

  const filteredLocations = locations.filter((location) => {
    const locationName = location.name || location.locationNickname || '';
    const locationAddress = getLocationAddress(location);
    const matchesSearch = !searchQuery ||
      locationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      locationAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.locationId?.toString().includes(searchQuery);

    const matchesOrg = !selectedOrgId || location.orgId === parseInt(selectedOrgId);
    const matchesStatus = !statusFilter || location.status === statusFilter;

    return matchesSearch && matchesOrg && matchesStatus;
  });

  const stats = {
    total: locations.length,
    approved: locations.filter((l) => l.status === 'approved').length,
    pending: locations.filter((l) => l.status === 'pending').length,
    inactive: locations.filter((l) => l.status === 'inactive').length,
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>
            <FiMapPin />
            Locations
          </Title>
          <Subtitle>Manage organization locations across the system</Subtitle>
        </HeaderContent>
        <AddButton onClick={() => setIsModalOpen(true)}>
          <FiPlus />
          Add Location
        </AddButton>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total Locations</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#10b981' }}>{stats.approved}</StatValue>
          <StatLabel>Approved</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#f59e0b' }}>{stats.pending}</StatValue>
          <StatLabel>Pending</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#ef4444' }}>{stats.inactive}</StatValue>
          <StatLabel>Inactive</StatLabel>
        </StatCard>
      </StatsContainer>

      <FiltersContainer>
        <SearchBar>
          <FiSearch />
          <SearchInput
            type="text"
            placeholder="Search by name, address, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>

        <FilterGroup>
          <FilterLabel>
            <FiFilter />
            Filters:
          </FilterLabel>
          <Select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
            <option value="">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.orgId} value={org.orgId.toString()}>
                {org.name}
              </option>
            ))}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
          </Select>
        </FilterGroup>
      </FiltersContainer>

      {loading ? (
        <LoadingContainer>
          <LoadingText>Loading locations...</LoadingText>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
        </ErrorContainer>
      ) : filteredLocations.length === 0 ? (
        <EmptyState>
          <FiMapPin width="48" height="48" />
          <EmptyTitle>No locations found</EmptyTitle>
          <EmptyText>
            {searchQuery || selectedOrgId || statusFilter
              ? 'Try adjusting your filters'
              : 'Get started by adding your first location'}
          </EmptyText>
        </EmptyState>
      ) : (
        <LocationsGrid>
          {filteredLocations.map((location) => (
            <LocationCard key={location.locationId || location._id}>
              <LocationHeader>
                <LocationIcon>
                  <FiMapPin />
                </LocationIcon>
                <LocationInfo>
                  <LocationName>{location.name || location.locationNickname}</LocationName>
                  <LocationOrg>{getOrgName(location.orgId)}</LocationOrg>
                </LocationInfo>
                <HeaderActions>
                  <StatusBadge $status={location.status || 'approved'}>
                    {location.status || 'approved'}
                  </StatusBadge>
                  <EditButton onClick={() => handleEditLocation(location)} title="Edit location">
                    <FiEdit2 />
                  </EditButton>
                </HeaderActions>
              </LocationHeader>
              <LocationBody>
                <LocationDetail>
                  <DetailLabel>Address:</DetailLabel>
                  <DetailValue>{getLocationAddress(location)}</DetailValue>
                </LocationDetail>
                {location.city && (
                  <LocationDetail>
                    <DetailLabel>City:</DetailLabel>
                    <DetailValue>{location.city}</DetailValue>
                  </LocationDetail>
                )}
                {location.state && (
                  <LocationDetail>
                    <DetailLabel>State:</DetailLabel>
                    <DetailValue>{location.state}</DetailValue>
                  </LocationDetail>
                )}
                {location.pinCode && (
                  <LocationDetail>
                    <DetailLabel>Pin Code:</DetailLabel>
                    <DetailValue>{location.pinCode}</DetailValue>
                  </LocationDetail>
                )}
                {location.latitude && location.longitude && (
                  <LocationDetail>
                    <DetailLabel>Coordinates:</DetailLabel>
                    <DetailValue>
                      {typeof location.latitude === 'number' ? location.latitude.toFixed(6) : Number(location.latitude).toFixed(6)}, {typeof location.longitude === 'number' ? location.longitude.toFixed(6) : Number(location.longitude).toFixed(6)}
                    </DetailValue>
                  </LocationDetail>
                )}
                {location.locationType && (
                  <LocationDetail>
                    <DetailLabel>Type:</DetailLabel>
                    <DetailValue>{location.locationType}</DetailValue>
                  </LocationDetail>
                )}
                {location.area && (
                  <LocationDetail>
                    <DetailLabel>Area:</DetailLabel>
                    <DetailValue>{location.area} sq ft</DetailValue>
                  </LocationDetail>
                )}
              </LocationBody>
              <LocationFooter>
                <FooterText>ID: {location.locationId}</FooterText>
                {location.createdAt && (
                  <FooterText>
                    Added: {new Date(location.createdAt).toLocaleDateString()}
                  </FooterText>
                )}
              </LocationFooter>
            </LocationCard>
          ))}
        </LocationsGrid>
      )}

      <AddLocationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleAddSuccess}
        organizations={organizations}
      />

      <EditLocationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedLocation(null);
        }}
        onSuccess={handleEditSuccess}
        location={selectedLocation}
        organizations={organizations}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}40;
  }

  svg {
    font-size: 18px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 300px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 18px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    font-size: 16px;
  }
`;

const Select = styled.select`
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const ErrorText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.error};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    opacity: 0.5;
    margin-bottom: 16px;
  }
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
`;

const LocationCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}20;
    transform: translateY(-2px);
  }
`;

const LocationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const LocationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 20px;
  }
`;

const LocationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LocationName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LocationOrg = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    font-size: 16px;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${({ $status }) =>
    $status === 'approved' ? '#10b98120' :
    $status === 'pending' ? '#f59e0b20' :
    '#ef444420'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#10b981' :
    $status === 'pending' ? '#f59e0b' :
    '#ef4444'};
  flex-shrink: 0;
`;

const LocationBody = styled.div`
  padding: 20px;
`;

const LocationDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  text-align: right;
  word-break: break-word;
`;

const LocationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const FooterText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
